import { useState } from "react";
import { usePortal, type ClientAppRow, type ClientDetail, type UpdateClientAppBody } from "@app/portal-sdk";
import { Alert, AppCard, AppIconTile, Box, Button, FormControlLabel, Stack, Switch, TextField, Typography } from "@app/ui-kit";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { APP_DEFINITIONS } from "../../apps/registry";
import { LoadingScreen } from "../../components/LoadingScreen";
import { errorMessage, useApiQuery } from "../../lib/useApiQuery";

type Props = {
  client: ClientDetail;
  /** Called after an entitlement changes so the parent can refresh enabledApps. */
  onChange: () => void;
};

export const AppsTab = ({ client, onChange }: Props) => {
  const { api } = usePortal();
  const apps = useApiQuery<ClientAppRow[]>(
    () => api.request<ClientAppRow[]>(`/admin/clients/${client.id}/apps`),
    [api, client.id]
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");

  const resetChannelAnalytics = async () => {
    setBusy("channel-analytics");
    setError(null);
    try {
      await api.request<void>(`/clients/${client.id}/apps/channel-analytics/reset`, { method: "POST", json: { confirm: resetConfirm } });
      setResetOpen(false);
      setResetConfirm("");
      onChange();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  // CLD-5: Customer Management's sample mode is its own flag on its own entitlement row, and its reset is its own.
  const [cmResetOpen, setCmResetOpen] = useState(false);
  const [cmResetConfirm, setCmResetConfirm] = useState("");
  const setCustomerSampleMode = async (enabled: boolean) => {
    setBusy("customer-management");
    setError(null);
    try {
      await api.request<{ sampleMode: boolean }>(`/clients/${client.id}/apps/customer-management/sample-mode`, { method: "PUT", json: { enabled } });
      apps.setData((rows) => (rows ?? []).map((r) => (r.slug === "customer-management" ? { ...r, settings: { ...r.settings, sampleMode: enabled } } : r)));
      onChange();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };
  const resetCustomerManagement = async () => {
    setBusy("customer-management");
    setError(null);
    try {
      await api.request<{ removed: Record<string, number> }>(`/clients/${client.id}/apps/customer-management/reset`, { method: "POST", json: { confirm: cmResetConfirm } });
      setCmResetOpen(false);
      setCmResetConfirm("");
      onChange();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const [reseeded, setReseeded] = useState<number | null>(null);
  const reseedVocabulary = async () => {
    setBusy("channel-analytics");
    setError(null);
    try {
      const r = await api.request<{ seeded: number }>(`/clients/${client.id}/apps/channel-analytics/vocabulary/reseed`, { method: "POST" });
      setReseeded(r.seeded);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const toggle = async (slug: ClientAppRow["slug"], enabled: boolean, settings?: Record<string, unknown>) => {
    setBusy(slug);
    setError(null);
    try {
      const body: UpdateClientAppBody = { enabled, settings };
      const row = await api.request<ClientAppRow>(`/admin/clients/${client.id}/apps/${slug}`, { method: "PUT", json: body });
      apps.setData((rows) => (rows ?? []).map((r) => (r.slug === slug ? row : r)));
      onChange();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  if (apps.loading && !apps.data) return <LoadingScreen minHeight={200} />;

  const enabledBySlug = new Map((apps.data ?? []).map((row) => [row.slug, row.enabled]));
  const settingsBySlug = new Map((apps.data ?? []).map((row) => [row.slug, row.settings]));

  return (
    <Stack spacing={3}>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        Applications this client can open from their portal. Turning one off hides it immediately.
      </Typography>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {apps.error && <Alert severity="error">{apps.error}</Alert>}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
        {APP_DEFINITIONS.map((def) => {
          const enabled = enabledBySlug.get(def.slug) ?? false;
          const settings = settingsBySlug.get(def.slug) ?? {};
          const sampleMode = Boolean(settings.sampleMode);
          return (
            <AppCard
              key={def.slug}
              eyebrow={enabled ? "Enabled" : "Off"}
              title={def.name}
              subtitle={def.description}
              icon={<AppIconTile icon={def.icon} color={def.iconColor} />}
              sx={{ opacity: enabled ? 1 : 0.6 }}
              action={
                <Switch
                  checked={enabled}
                  onChange={(event) => void toggle(def.slug, event.target.checked)}
                  disabled={busy === def.slug}
                  inputProps={{ "aria-label": `Enable ${def.name}` }}
                />
              }
            >
              {def.slug === "customer-management" && enabled && (
                <Stack spacing={0.5}>
                  <FormControlLabel
                    control={<Switch size="small" checked={sampleMode} onChange={(event) => void setCustomerSampleMode(event.target.checked)} disabled={busy === def.slug} />}
                    label={<Typography variant="body2">Illustrative sample data mode</Typography>}
                  />
                  <Typography variant="caption" sx={{ color: "text.disabled" }}>Marks every screen as illustrative. Load the sample workbook from the Load screen.</Typography>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ pt: 1 }} flexWrap="wrap" useFlexGap>
                    <Button size="small" variant="text" color="error" onClick={() => setCmResetOpen(true)} disabled={busy === def.slug}>
                      Reset Customer Management data…
                    </Button>
                  </Stack>
                </Stack>
              )}
              {def.slug === "due-diligence" && enabled && (
                <Stack spacing={0.5}>
                  {/* LIB-6, DESIGN section 3 answer 8: whether this client may propose questions for the shared library. Default off. */}
                  <FormControlLabel
                    control={<Switch size="small" checked={Boolean(settings.mayProposeQuestions)} onChange={(event) => void toggle(def.slug, true, { mayProposeQuestions: event.target.checked })} disabled={busy === def.slug} inputProps={{ "aria-label": "Clients may propose questions for the library" }} />}
                    label={<Typography variant="body2">May propose questions for the library</Typography>}
                  />
                  <Typography variant="caption" sx={{ color: "text.disabled" }}>Shows a Propose control on the client's own questions. A proposal sits in the library's queue until BKDP accepts or declines it.</Typography>
                  <FormControlLabel
                    control={<Switch size="small" checked={sampleMode} onChange={(event) => void toggle(def.slug, true, { sampleMode: event.target.checked })} disabled={busy === def.slug} />}
                    label={<Typography variant="body2">Illustrative sample data mode</Typography>}
                  />
                </Stack>
              )}
              {def.slug === "channel-analytics" && enabled && (
                <Stack spacing={0.5}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={sampleMode}
                        onChange={(event) => void toggle(def.slug, true, { sampleMode: event.target.checked })}
                        disabled={busy === def.slug}
                      />
                    }
                    label={<Typography variant="body2">Illustrative sample data mode</Typography>}
                  />
                  <Typography variant="caption" sx={{ color: "text.disabled" }}>
                    Marks every screen with a "Sample data" chip so illustrative figures are never mistaken for the client's. Reset before turning it off.
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    The client's fiscal calendar, profile and reference data are managed on the Settings tab.
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ pt: 1 }} flexWrap="wrap" useFlexGap>
                    <Button size="small" variant="text" onClick={() => void reseedVocabulary()} disabled={busy === def.slug}>
                      Re-seed vocabulary
                    </Button>
                    {reseeded !== null && <Typography variant="caption" sx={{ color: "text.secondary" }}>{reseeded} model values refreshed; client-added values untouched.</Typography>}
                    <Button size="small" variant="text" color="error" onClick={() => setResetOpen(true)} disabled={busy === def.slug}>
                      Reset Channel Analytics data…
                    </Button>
                  </Stack>
                </Stack>
              )}
            </AppCard>
          );
        })}
      </Box>
      <Dialog open={cmResetOpen} onClose={() => setCmResetOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Customer Management for {client.name}?</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Every organization, person, location, channel link and interaction is removed, hard. The reference values stay, and so do the people who own a channel in Commercial Model. Type the portal address <code>{client.slug}</code> to confirm.
            </Typography>
            <TextField value={cmResetConfirm} onChange={(e) => setCmResetConfirm(e.target.value)} placeholder={client.slug} size="small" autoFocus />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="text" onClick={() => setCmResetOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => void resetCustomerManagement()} disabled={cmResetConfirm !== client.slug || busy !== null}>Reset</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={resetOpen} onClose={() => setResetOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Channel Analytics for {client.name}?</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Every loaded row, every decision and the channel architecture are removed. The seeded channels stay. Type the portal address <code>{client.slug}</code> to confirm.
            </Typography>
            <TextField value={resetConfirm} onChange={(e) => setResetConfirm(e.target.value)} placeholder={client.slug} size="small" autoFocus />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="text" onClick={() => setResetOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => void resetChannelAnalytics()} disabled={resetConfirm !== client.slug || busy !== null}>Reset</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
